import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import { PROJECTS } from "@/lib/index";
import { ProjectCard } from "@/components/ProjectCard";
import Layout from "@/components/Layout";

type FilterCategory = "all" | "brand" | "corporate" | "social";

export default function PortfolioPage() {
  const { lang } = useLangStore();
  const t = translations[lang];
  const [activeFilter, setActiveFilter] = useState<FilterCategory>("all");

  const filters: { key: FilterCategory; label: string }[] = [
    { key: "all", label: t.portfolio.filterAll },
    { key: "brand", label: t.portfolio.filterBrand },
    { key: "corporate", label: t.portfolio.filterCorporate },
    { key: "social", label: t.portfolio.filterSocial },
  ];

  const filtered = activeFilter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === activeFilter);

  return (
    <Layout>
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-primary font-semibold text-sm uppercase tracking-wider mb-2"
            >
              ✦ {lang === "en" ? "My Work" : "أعمالي"}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl lg:text-5xl font-extrabold text-foreground mb-3"
            >
              {t.portfolio.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground text-lg max-w-xl mx-auto"
            >
              {t.portfolio.subtitle}
            </motion.p>
          </div>

          {/* Filter tabs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-2 mb-12"
          >
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeFilter === f.key
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </motion.div>

          {/* Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            >
              {filtered.map((project, i) => (
                <ProjectCard key={project.id} project={project} index={i} />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">
              {lang === "en" ? "No projects in this category yet." : "لا توجد مشاريع في هذه الفئة حتى الآن."}
            </div>
          )}

          {/* Count */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center text-sm text-muted-foreground mt-10"
          >
            {lang === "en"
              ? `Showing ${filtered.length} of ${PROJECTS.length} projects`
              : `عرض ${filtered.length} من ${PROJECTS.length} مشاريع`}
          </motion.p>
        </div>
      </div>
    </Layout>
  );
}
